/**
 * Colour palettes tab.
 *
 * Works at whatever scope the canvas implies: a selected frame is recoloured alone,
 * and with nothing selected every frame changes together — so a two-sided card can
 * be restyled in one click.
 *
 * Three kinds of control, because "change the colours" means all of these in practice:
 *  - PRESETS: the colours the design is ACTUALLY made of, read back off the scope
 *    (utils/colorUsage). Editing one rewrites every place that paints it, so a
 *    swatch here works like a global variable for the frame — the answer to "the
 *    black should be navy", which a whole-artwork palette can't express.
 *  - PALETTES: recolour the artwork onto a chosen ramp. Vector colours are remapped
 *    as values (utils/paletteRecolor) and image pixels as a gradient map
 *    (utils/paletteImage), both keyed off lightness so the design keeps its
 *    contrast. Originals are snapshotted, so Reset is exact.
 *  - TONE: hue / saturation / brightness written onto every element in scope as the
 *    same `hueRotate` / `saturate` / `brightness` style keys the Adjust popover
 *    uses — the canvas, the export and undo all handle them with existing code.
 */
import { useMemo, useRef, useState } from 'react';
import { IconRotate2, IconPalette } from '@tabler/icons-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
    PanelSection, FigSlider, T,
} from '../../ui/figma';
import { ColorPicker } from '../../ui/ColorControl';
import { COLOR_ADJUST_DEFAULTS } from '../propertyControls';
import { COLOR_PALETTES } from '../../schema/colorPalettes';
import {
    COLOR_GROUPS, collectColorUsage, colorKey, hasColorPresetEdits, newColorToken,
} from '../../utils/colorUsage';

const num = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);

/**
 * Section reset — the word first, then the icon.
 *
 * A bare ↺ in a section header is ambiguous the moment there are two of them:
 * each section owns its own undo, and the label is what says which.
 */
function ResetBtn({ title, disabled, onClick }) {
    return (
        <button
            type="button"
            title={title}
            disabled={disabled}
            onClick={onClick}
            className={`h-6 px-1.5 flex items-center gap-1 rounded-[5px] text-[11px] font-medium
                transition-colors disabled:opacity-30 disabled:pointer-events-none
                ${T.hoverBg} text-black/80 dark:text-white/80`}
        >
            Reset
            <IconRotate2 size={13} stroke={1.75} />
        </button>
    );
}

/** Hex box for one preset row — commits on Enter / blur, never mid-keystroke. */
function HexField({ value, onCommit }) {
    const [draft, setDraft] = useState(null);
    const shown = (draft ?? value).replace(/^#/, '').toUpperCase();
    const commit = () => {
        const text = draft;
        setDraft(null);
        if (text == null) return;
        const next = `#${text.replace(/^#/, '').trim()}`;
        if (colorKey(next) && colorKey(next) !== colorKey(value)) onCommit(next);
    };
    return (
        <input
            value={shown}
            spellCheck={false}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') { setDraft(null); e.currentTarget.blur(); }
            }}
            className={`flex-1 min-w-0 h-6 px-1 bg-transparent outline-none text-[11px] tabular-nums ${T.text}`}
        />
    );
}

/**
 * "Selection colour presets" — every colour in scope, each one editable globally.
 *
 * Two things this has to get right, both learned the hard way:
 *
 *  - A row is an IDENTITY, not a colour value. Dragging one row's colour through
 *    another's used to merge the two for good, so the design lost a global every
 *    time two of them met. Rows are now keyed by the token utils/colorUsage
 *    stamps on the artwork, and a token is minted here so an edit that starts on
 *    an untouched colour keeps ONE identity from the first drag to the last.
 *  - The list is DERIVED, so an edit changes the very list it came from, and a
 *    row re-keying itself under an open picker would slam the popover shut
 *    mid-drag. So the list is frozen while a picker is open, and the row being
 *    edited is painted from the live value instead.
 */
function ColorPresets({ frames = [], elements = [], onReplace }) {
    const usage = useMemo(() => collectColorUsage(frames, elements), [frames, elements]);

    // Only the kinds that actually paint something get a tab — an all-text card
    // has no business showing an empty Images tab.
    const tabs = useMemo(() => COLOR_GROUPS
        .map((g) => ({ ...g, count: usage.filter((r) => r.group === g.id).length }))
        .filter((g) => g.count), [usage]);

    const [group, setGroup] = useState(null);
    const activeGroup = tabs.some((t) => t.id === group) ? group : tabs[0]?.id;

    const frozen = useRef(null);
    const [openId, setOpenId] = useState(null);
    // The open row's live identity and colour, held in a ref AS WELL as state.
    // Dragging the picker emits several times inside one React batch, and each
    // swap has to be aimed at what the document holds THIS instant — reading it
    // from state meant aiming at a row that the previous emit had already
    // renamed, so every move after the first in a batch matched nothing and the
    // drag stopped following the cursor.
    const liveRef = useRef(null);
    const [live, setLive] = useState(null);

    const all = openId ? (frozen.current || usage) : usage;
    const rows = all.filter((r) => r.group === activeGroup);

    const setOpen = (row, isOpen) => {
        if (isOpen) {
            frozen.current = usage;
            // One token for the whole edit: minted now if this colour has never
            // been touched, so a drag doesn't split into a new global per frame.
            liveRef.current = { id: row.id, color: row.color, token: row.token || newColorToken() };
            setOpenId(row.id);
            setLive(row.color);
        } else {
            frozen.current = null;
            liveRef.current = null;
            setOpenId(null);
            setLive(null);
        }
    };

    // What this row paints RIGHT NOW: the live value while its picker is open,
    // since after the first change the document no longer holds the row's colour.
    const shownColor = (row) => (openId === row.id && live ? live : row.color);

    const change = (row, next) => {
        const isOpen = openId === row.id;
        const from = isOpen ? liveRef.current : { id: row.id, color: row.color, token: row.token };
        if (!colorKey(next) || colorKey(next) === colorKey(from.color)) return;
        const token = from.token || newColorToken();
        onReplace(from.id, next, token);
        // The row now answers to its token and its new colour — that is what the
        // next emit of this same drag has to aim at.
        if (isOpen) {
            liveRef.current = { id: `t:${token}:${colorKey(next)}`, color: next, token };
            setLive(next);
        }
    };

    if (!rows.length) {
        return (
            <p className={`text-[10px] leading-snug ${T.textFaint}`}>
                No colours yet — add artwork to a frame first.
            </p>
        );
    }

    return (
        <>
            <div className="flex flex-wrap gap-1 mb-2">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setGroup(t.id)}
                        className={`h-6 px-2 rounded-[5px] text-[11px] font-medium transition-colors
                            ${t.id === activeGroup
                            ? 'bg-[#0d99ff] text-white'
                            : `${T.inputBg} ${T.hoverBg} text-black/70 dark:text-white/70`}`}
                    >
                        {t.label}
                        <span className="ml-1 tabular-nums opacity-60">{t.count}</span>
                    </button>
                ))}
            </div>

            <div className="flex flex-col gap-1.5 py-0.5">
                {rows.map((row) => {
                    const color = shownColor(row);
                    return (
                        <div
                            key={row.id}
                            className={`flex items-center gap-1 h-7 rounded-[5px] border border-transparent ${T.inputBg}
                                hover:border-[#e6e6e6] dark:hover:border-[#555555]
                                focus-within:!border-[#0d99ff] focus-within:ring-1 focus-within:ring-[#0d99ff] transition-colors`}
                        >
                            <Popover open={openId === row.id} onOpenChange={(o) => setOpen(row, o)}>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        title={`Change this colour everywhere it is used`}
                                        className="ml-1.5 h-4 w-4 shrink-0 rounded-[3px] border border-black/10 dark:border-white/20"
                                        style={{ backgroundColor: color }}
                                    />
                                </PopoverTrigger>
                                <PopoverContent
                                    side="left"
                                    align="start"
                                    sideOffset={12}
                                    onOpenAutoFocus={(e) => e.preventDefault()}
                                    className="w-auto p-3 rounded-[13px] border-[#e6e6e6] dark:border-[#444444] dark:bg-[#2c2c2c] shadow-xl"
                                >
                                    <div className={`text-[11px] font-semibold mb-2 ${T.text}`}>
                                        Replace everywhere
                                    </div>
                                    <ColorPicker value={color} onChange={(v) => change(row, v)} />
                                </PopoverContent>
                            </Popover>
                            <HexField value={color} onCommit={(v) => change(row, v)} />
                            <span
                                title={`Painted in ${row.count} place${row.count === 1 ? '' : 's'}`}
                                className={`shrink-0 mr-2 text-[10px] tabular-nums ${T.textFaint}`}
                            >
                                {row.count}
                            </span>
                        </div>
                    );
                })}
            </div>

            <p className={`mt-1.5 text-[10px] leading-snug ${T.textFaint}`}>
                The colours this tab&apos;s elements are painted with, most-used first. Changing
                one rewrites it everywhere in that tab at once, so it behaves like a global
                colour. Each row keeps its own identity: giving two of them the same colour
                does not join them, and moving one back apart leaves both intact.
                Transparency is kept.
            </p>
        </>
    );
}

export default function ColorPalettesPanel({
    frames = [],
    scopeFrames = [],
    scopeLabel = 'All frames',
    elements = [],
    onApplyPalette,
    onClearPalette,
    onReplaceColor,
    onResetColors,
    onAdjust,
    onResetAdjust,
}) {
    if (!frames.length) {
        return (
            <PanelSection title="Colour palettes">
                <p className={`text-[11px] leading-relaxed ${T.textSoft}`}>
                    No frames yet. Add one (press F) and its colours can be set here.
                </p>
            </PanelSection>
        );
    }

    // Show the tone sliders at the scope's current value. Mixed values across a
    // multi-frame scope read as the FIRST element's — moving the slider then writes
    // one value to all of them, which is what "apply to every frame" means.
    const sample = elements[0]?.styles || {};
    const hue = num(sample.hueRotate, COLOR_ADJUST_DEFAULTS.hueRotate);
    const sat = num(sample.saturate, COLOR_ADJUST_DEFAULTS.saturate);
    const bri = num(sample.brightness, COLOR_ADJUST_DEFAULTS.brightness);
    const toned = hue !== COLOR_ADJUST_DEFAULTS.hueRotate
        || sat !== COLOR_ADJUST_DEFAULTS.saturate
        || bri !== COLOR_ADJUST_DEFAULTS.brightness;

    // The palette in use across the scope (null when the frames disagree, or when
    // none has been applied), so the active ring tells the truth.
    const appliedId = scopeFrames.length
        && scopeFrames.every((f) => f.paletteId && f.paletteId === scopeFrames[0].paletteId)
        ? scopeFrames[0].paletteId
        : null;

    /**
     * A palette reads as the ramp itself, not one colour: the circle shows light →
     * mid → dark, which is exactly how the design will be remapped.
     */
    const swatch = (p, active) => {
        const stops = [p.colors[1], p.colors[4], p.colors[7]].filter(Boolean);
        return (
            <span
                title={p.name}
                aria-label={p.name}
                className={`inline-flex h-10 w-10 rounded-full border ${active ? 'border-[#0d99ff] ring-2 ring-[#0d99ff]/40' : 'border-black/10 dark:border-white/15'}`}
                style={{ backgroundImage: `linear-gradient(140deg, ${stops.join(', ')})` }}
            />
        );
    };

    return (
        <>
            <PanelSection title="Applies to">
                <div className="flex items-center gap-1.5">
                    <IconPalette size={13} className={`shrink-0 ${T.textSoft}`} stroke={1.75} />
                    <span className={`text-[11px] font-medium ${T.text}`}>{scopeLabel}</span>
                </div>
                <p className={`mt-1.5 text-[10px] leading-snug ${T.textFaint}`}>
                    Select a frame on canvas to recolour just that one; with nothing
                    selected every frame changes together.
                </p>
            </PanelSection>

            <PanelSection
                title="Selection colour presets"
                actions={(
                    // Presets only. The palette below keeps its own reset, because
                    // the two are separate edits of the same artwork.
                    <ResetBtn
                        title="Back to the colours these elements were drawn with"
                        disabled={!hasColorPresetEdits(scopeFrames, elements)}
                        onClick={onResetColors}
                    />
                )}
            >
                <ColorPresets
                    frames={scopeFrames}
                    elements={elements}
                    onReplace={onReplaceColor}
                />
            </PanelSection>

            <PanelSection
                title="Palettes"
                actions={(
                    // Palette only. The tone sliders below are a separate edit and
                    // keep their own reset — dropping both from here meant losing a
                    // hand-tuned hue just to go back to the original artwork.
                    <ResetBtn
                        title="Back to the original artwork colours"
                        disabled={!appliedId}
                        onClick={onClearPalette}
                    />
                )}
            >
                <div className="flex flex-wrap gap-3 py-2">
                    {COLOR_PALETTES.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            title={`Recolour ${scopeLabel.toLowerCase()} with ${p.name}`}
                            onClick={() => onApplyPalette(p)}
                            className="rounded-full transition p-0 hover:scale-105"
                            style={{ minWidth: '40px', minHeight: '40px' }}
                        >
                            {swatch(p, appliedId === p.id)}
                        </button>
                    ))}
                </div>
                <p className={`mt-1 text-[10px] leading-snug ${T.textFaint}`}>
                    Recolours the artwork itself — shapes, text, backgrounds and imported
                    images alike. Every colour moves onto the palette by its own lightness,
                    so the layout keeps its contrast. Whites stay white, so paper and
                    reversed-out logos are left alone. Reset brings the original artwork
                    colours back and leaves the tone below alone.
                </p>
            </PanelSection>

            <PanelSection
                title="Custom"
                actions={(
                    // Tone only — the applied palette stays put.
                    <ResetBtn
                        title="Back to hue 0, saturation 100, brightness 100"
                        disabled={!toned}
                        onClick={onResetAdjust}
                    />
                )}
            >
                {elements.length ? (
                    <>
                        <FigSlider
                            label="Hue"
                            value={hue}
                            min={-180}
                            max={180}
                            defaultValue={COLOR_ADJUST_DEFAULTS.hueRotate}
                            onChange={(v) => onAdjust({ hueRotate: v })}
                        />
                        <FigSlider
                            label="Saturation"
                            value={sat}
                            min={0}
                            max={200}
                            defaultValue={COLOR_ADJUST_DEFAULTS.saturate}
                            onChange={(v) => onAdjust({ saturate: v })}
                        />
                        <FigSlider
                            label="Brightness"
                            value={bri}
                            min={20}
                            max={200}
                            defaultValue={COLOR_ADJUST_DEFAULTS.brightness}
                            onChange={(v) => onAdjust({ brightness: v })}
                        />
                        <p className={`mt-1.5 text-[10px] leading-snug ${T.textFaint}`}>
                            Fine-tune hue, saturation and brightness — on their own, or on top
                            of a palette. Reset here clears only these three.
                        </p>
                    </>
                ) : (
                    <p className={`text-[10px] leading-snug ${T.textFaint}`}>
                        Nothing to tone yet — add artwork to a frame first.
                    </p>
                )}
            </PanelSection>
        </>
    );
}
